/**
 * Partner Application Page — Cafe owners apply to join the CoffeeMates network.
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Container, Typography, TextField, Button, Paper, Stepper, Step, StepLabel,
  MenuItem, Select, FormControl, InputLabel, Chip, Alert, CircularProgress, Grid,
} from '@mui/material';
import { Storefront, CheckCircle } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import eventsService from '../../services/eventsService';
import partnerService from '../../services/partnerService';
import { colors, designTokens } from '../../theme/theme';
import { useAuth } from '../../contexts/AuthContext';

const STEPS = ['Venue Info', 'Contact & Details', 'Review & Submit'];

const defaultForm = {
  venue_name: '', address: '', zone_id: '', tier: 'standard',
  description: '', contact_phone: '', contact_email: '', photo_url: '', why_join: '',
};

export default function PartnerApplyPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({ ...defaultForm, contact_email: user?.email || '' });
  const [zones, setZones] = useState<any[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { eventsService.getZones().then(setZones).catch(() => { /* no-op */ }); }, []);

  const update = (field: string, value: string) => setForm(f => ({ ...f, [field]: value }));

  const canNext = () => {
    if (step === 0) return form.venue_name && form.address && form.zone_id;
    if (step === 1) return true;
    return true;
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError('');
    try {
      await partnerService.applyAsPartner(form);
      setSuccess(true);
    } catch (e: any) {
      console.warn('[PartnerApplyPage] submit application failed', e);
      setError(e.message || 'Failed to submit application');
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: colors.background.default }}>
        <Paper elevation={0} sx={{ p: 5, maxWidth: 500, textAlign: 'center', borderRadius: 5, bgcolor: designTokens.glass.surface, border: `1px solid ${designTokens.glass.border}` }}>
          <CheckCircle sx={{ fontSize: 64, color: colors.success.main, mb: 2 }} />
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#FFF', mb: 1 }}>{t('pages.partner_apply.success_title')}</Typography>
          <Typography sx={{ color: colors.neutral[400], mb: 3 }}>We'll review your application within 48 hours and keep you updated at every step.</Typography>
          <Button variant="contained" onClick={() => navigate('/partner/application-status')} sx={{ mr: 1, borderRadius: 3 }}>Track Status</Button>
          <Button variant="outlined" onClick={() => navigate('/')} sx={{ borderRadius: 3, color: colors.neutral[400] }}>Back to Home</Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: colors.background.default, py: 4 }}>
      <Container maxWidth="sm">
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Storefront sx={{ fontSize: 48, color: colors.primary.main, mb: 1 }} />
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#FFF' }}>{t('pages.partner_apply.title')}</Typography>
          <Typography sx={{ color: colors.neutral[400] }}>Join the CoffeeMates network and host coffee meetup events</Typography>
        </Box>

        <Stepper activeStep={step} sx={{ mb: 4, '& .MuiStepLabel-label': { color: colors.neutral[400] }, '& .Mui-active .MuiStepLabel-label': { color: '#FFF' } }}>
          {STEPS.map(label => <Step key={label}><StepLabel>{label}</StepLabel></Step>)}
        </Stepper>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Paper elevation={0} sx={{ p: 3, borderRadius: 4, bgcolor: designTokens.glass.surface, border: `1px solid ${designTokens.glass.border}` }}>
          {step === 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField label="Venue Name *" value={form.venue_name} onChange={e => update('venue_name', e.target.value)} fullWidth />
              <TextField label="Address *" value={form.address} onChange={e => update('address', e.target.value)} fullWidth />
              <FormControl fullWidth>
                <InputLabel>Zone *</InputLabel>
                <Select value={form.zone_id} onChange={e => update('zone_id', e.target.value)} label="Zone *">
                  {zones.map(z => <MenuItem key={z.id} value={z.id}>{z.name}</MenuItem>)}
                </Select>
              </FormControl>
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, color: colors.neutral[400] }}>Tier</Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {['standard', 'premium'].map(t => (
                    <Chip key={t} label={t} onClick={() => update('tier', t)} variant={form.tier === t ? 'filled' : 'outlined'}
                      color={form.tier === t ? 'primary' : 'default'} sx={{ textTransform: 'capitalize', cursor: 'pointer' }} />
                  ))}
                </Box>
              </Box>
              <TextField label="Description" value={form.description} onChange={e => update('description', e.target.value)} fullWidth multiline rows={3} />
            </Box>
          )}

          {step === 1 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField label="Contact Phone" value={form.contact_phone} onChange={e => update('contact_phone', e.target.value)} fullWidth placeholder="+994 XX XXX XX XX" />
              <TextField label="Contact Email" value={form.contact_email} onChange={e => update('contact_email', e.target.value)} fullWidth />
              <TextField label="Photo URL (optional)" value={form.photo_url} onChange={e => update('photo_url', e.target.value)} fullWidth placeholder="https://..." />
              <TextField label="Why join CoffeeMates?" value={form.why_join} onChange={e => update('why_join', e.target.value)} fullWidth multiline rows={3} placeholder="Tell us about your venue and why you'd be a great fit..." />
            </Box>
          )}

          {step === 2 && (
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#FFF' }}>Review Your Application</Typography>
              <Grid container spacing={1}>
                {[
                  ['Venue', form.venue_name], ['Address', form.address],
                  ['Zone', zones.find(z => z.id === form.zone_id)?.name || form.zone_id],
                  ['Tier', form.tier], ['Phone', form.contact_phone || '—'], ['Email', form.contact_email || '—'],
                ].map(([label, val]) => (
                  <Grid item xs={6} key={label as string}>
                    <Typography variant="caption" sx={{ color: colors.neutral[500] }}>{label}</Typography>
                    <Typography variant="body2" sx={{ color: '#FFF', fontWeight: 600 }}>{val as string}</Typography>
                  </Grid>
                ))}
              </Grid>
              {form.description && <Box sx={{ mt: 2 }}><Typography variant="caption" sx={{ color: colors.neutral[500] }}>Description</Typography><Typography variant="body2" sx={{ color: '#FFF' }}>{form.description}</Typography></Box>}
            </Box>
          )}
        </Paper>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
          <Button disabled={step === 0} onClick={() => setStep(s => s - 1)} sx={{ color: colors.neutral[400] }}>Back</Button>
          {step < 2 ? (
            <Button variant="contained" disabled={!canNext()} onClick={() => setStep(s => s + 1)} sx={{ borderRadius: 3 }}>Next</Button>
          ) : (
            <Button variant="contained" onClick={handleSubmit} disabled={submitting} sx={{ borderRadius: 3, px: 4 }}>
              {submitting ? <CircularProgress size={20} /> : 'Submit Application'}
            </Button>
          )}
        </Box>
      </Container>
    </Box>
  );
}
